import { FormField, FormSchema } from '../types/form';
import { evaluateConditionalLogic } from '../features/forms/utils/conditionalLogic';
import { evaluateCalculation } from '../features/forms/utils/calculations';

/** Returns a sample/example value for a field based on its type */
function getSampleValue(field: FormField): any {
  if (field.defaultValue) return field.defaultValue;
  switch (field.type) {
    case 'email':    return 'example@email.com';
    case 'tel':      return '+1 555-000-0000';
    case 'number':   return field.validation?.min ?? 1;
    case 'date':     return new Date().toISOString().split('T')[0];
    case 'checkbox': return false;
    case 'select':
    case 'radio':
    case 'option':   return field.options?.[0]?.value ?? 'option_1';
    case 'textarea': return 'Sample text content';
    case 'signature': return '[signature data]';
    case 'file':     return '[file attachment]';
    default:         return 'Sample text';
  }
}

/** Validates a value against a field's validation rules */
function validateFieldValue(field: FormField, value: any): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const v = field.validation;

  if (field.required && (value === '' || value === null || value === undefined)) {
    errors.push('Field is required');
  }

  if (v && value !== '' && value !== null && value !== undefined) {
    if (v.minLength && String(value).length < v.minLength)
      errors.push(`Minimum length is ${v.minLength}`);
    if (v.maxLength && String(value).length > v.maxLength)
      errors.push(`Maximum length is ${v.maxLength}`);
    if (v.min !== undefined && Number(value) < v.min)
      errors.push(`Minimum value is ${v.min}`);
    if (v.max !== undefined && Number(value) > v.max)
      errors.push(`Maximum value is ${v.max}`);
    if (v.pattern && !new RegExp(v.pattern).test(String(value)))
      errors.push(v.patternMessage ?? `Does not match pattern: ${v.pattern}`);
  }

  return { valid: errors.length === 0, errors };
}

export interface FormJSONField {
  id: string;
  name: string;
  label: string;
  type: string;
  value: any;
  validation: {
    rules: {
      required?: boolean;
      minLength?: number;
      maxLength?: number;
      min?: number;
      max?: number;
      pattern?: string;
    };
    state: 'valid' | 'invalid';
    errors: string[];
  };
  conditional: {
    rules: Array<{
      field: string;
      operator: string;
      value: string | number;
      action: 'show' | 'hide';
    }>;
    isVisible: boolean;
  } | null;
  calculated: {
    formula: string;
    dependsOn: string[];
    result: number | null;
  } | null;
}

export interface FormJSONOutput {
  formTitle: string;
  formHandler: string;
  generatedAt: string;
  fields: FormJSONField[];
}

/**
 * Generates a structured JSON snapshot of the form schema + current values.
 * Falls back to sample values for empty fields.
 */
export function generateFormJSON(
  schema: Pick<FormSchema, 'title' | 'handler' | 'fields'> & { title: string; handler: string; fields: FormField[] },
  formValues: Record<string, any> = {}
): FormJSONOutput {
  const fields: FormJSONField[] = schema.fields.map((field) => {
    // Resolve value: use provided value, fall back to sample
    const rawValue = formValues[field.name];
    const value = rawValue !== undefined && rawValue !== '' ? rawValue : getSampleValue(field);

    // Calculated fields: compute result from current values
    let calculated: FormJSONField['calculated'] = null;
    if (field.type === 'calculated' && field.calculation) {
      calculated = {
        formula: field.calculation.formula,
        dependsOn: field.calculation.fields,
        result: evaluateCalculation(field.calculation, { ...formValues, [field.name]: value }),
      };
    }

    // Conditional logic: evaluate visibility
    let conditional: FormJSONField['conditional'] = null;
    if (field.conditionalLogic && field.conditionalLogic.length > 0) {
      conditional = {
        rules: field.conditionalLogic.map((c) => ({
          field: c.field,
          operator: c.operator,
          value: c.value,
          action: c.action,
        })),
        isVisible: evaluateConditionalLogic(field.conditionalLogic, formValues),
      };
    }

    // Validation
    const { valid, errors } = validateFieldValue(field, value);

    return {
      id: field.id,
      name: field.name,
      label: field.label,
      type: field.type,
      value,
      validation: {
        rules: {
          ...(field.required ? { required: true } : {}),
          ...(field.validation?.minLength !== undefined ? { minLength: field.validation.minLength } : {}),
          ...(field.validation?.maxLength !== undefined ? { maxLength: field.validation.maxLength } : {}),
          ...(field.validation?.min !== undefined ? { min: field.validation.min } : {}),
          ...(field.validation?.max !== undefined ? { max: field.validation.max } : {}),
          ...(field.validation?.pattern ? { pattern: field.validation.pattern } : {}),
        },
        state: valid ? 'valid' : 'invalid',
        errors,
      },
      conditional,
      calculated,
    };
  });

  return {
    formTitle: schema.title,
    formHandler: schema.handler,
    generatedAt: new Date().toISOString(),
    fields,
  };
}
